import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { OrderDetailDto } from '@crm/shared'
import { setOrderMoreInvoices } from '../../../shared/api/orders.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { Badge } from '../../../shared/ui/Badge'

/**
 * Сверка заказа со счётом 1С в карточке заказа (21.10.2026, docs/features/unf-invoice-upload.md):
 * «Сверен со счётом 1С» — все позиции в счетах, загруженных из 1С, новых счетов не ждём;
 * иначе — сколько позиций ждёт счёта. Переключатель «Будут ещё счета из 1С» — та же
 * отметка, что галочка в окне загрузки счёта.
 */
export function OrderInvoiceCheck({
  order,
  canMutate,
}: {
  order: OrderDetailDto
  canMutate: boolean
}) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (order.status === 'CANCELLED' || order.status === 'ARCHIVED') return null
  const waiting = order.positions.filter((p) => p.awaitingInvoice).length
  const anyUnf = order.invoices.some((i) => i.status !== 'CANCELLED' && i.amountSource === 'unf')

  const toggle = async (value: boolean) => {
    setBusy(true)
    try {
      const fresh = await setOrderMoreInvoices(order.id, value)
      qc.setQueryData(['orders', order.id], fresh)
      void qc.invalidateQueries({ queryKey: ['orders', order.id] })
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось изменить отметку'))
    } finally {
      setBusy(false)
    }
  }

  let badge: React.ReactNode
  if (order.invoiceReconciled) badge = <Badge color="green">Сверен со счётом 1С</Badge>
  else if (!anyUnf) badge = <Badge color="gray">Счёт из 1С ещё не загружен</Badge>
  else if (waiting > 0) badge = <Badge color="yellow">Ждут счёта из 1С: {waiting} поз.</Badge>
  else badge = <Badge color="yellow">Ждём ещё счета из 1С</Badge>

  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white p-3 text-sm">
      {badge}
      <label className="flex items-center gap-2 text-gray-700">
        <input
          type="checkbox"
          checked={order.moreInvoicesExpected}
          disabled={!canMutate || busy}
          onChange={(e) => void toggle(e.target.checked)}
        />
        Будут ещё счета из 1С
      </label>
      <span className="text-xs text-gray-500">
        В закупку пойдёт только заказ, сверенный со счётом 1С.
      </span>
    </div>
  )
}
